import { ref } from 'vue';
import { defineStore } from 'pinia';

const STORAGE_KEY = 'useNewAppShell';

/**
 * Subset av UserPreferences-featureFlags som appShellStore.hydrateFromBackend leser.
 * Holdes lokal og strukturell for å unngå avhengighet til UserPreferences-typen.
 */
export interface AppShellPreferenceFragment {
  featureFlags?: {
    useNewAppShell?: boolean | null;
  } | null;
}

/**
 * Save-callback for backend-persistens av useNewAppShell-flagget. Settes av
 * composable-laget (useUserPreferencesHydration).
 */
export type UseNewAppShellSaveCallback = (next: boolean) => void | Promise<void>;

function loadStoredFlag (): boolean {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'true') return true;
    if (stored === 'false') return false;
  } catch {
    // localStorage may be disabled — accept memory-only mode
  }
  // Opt-out (WI 2258): ny navigering er standard når brukeren ikke har valgt noe.
  return true;
}

function persistLocal (next: boolean): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, next ? 'true' : 'false');
  } catch {
    // localStorage may be disabled - accept memory-only mode
  }
}

export const useAppShellStore = defineStore('appShell', () => {
  const useNewAppShell = ref<boolean>(loadStoredFlag());
  const isHydrating = ref<boolean>(false);

  let saveCallback: UseNewAppShellSaveCallback | null = null;

  function setSaveCallback (callback: UseNewAppShellSaveCallback | null): void {
    saveCallback = callback;
  }

  function setUseNewAppShell (next: boolean): void {
    useNewAppShell.value = next;
    persistLocal(next);

    if (!isHydrating.value && saveCallback) {
      void saveCallback(next);
    }
  }

  /**
   * Hydrer useNewAppShell fra backend-preferences. Backend wins hvis verdien er
   * en boolean; fallback til eksisterende state (warm-cache) hvis null/undefined.
   */
  function hydrateFromBackend (prefs: AppShellPreferenceFragment | null | undefined): void {
    if (!prefs) return;

    isHydrating.value = true;
    try {
      const flag = prefs.featureFlags?.useNewAppShell;
      if (typeof flag === 'boolean') {
        setUseNewAppShell(flag);
      }
    } finally {
      isHydrating.value = false;
    }
  }

  return {
    useNewAppShell,
    isHydrating,
    setUseNewAppShell,
    hydrateFromBackend,
    setSaveCallback
  };
});
